import { EventEmitter, Inject, Injectable } from "@angular/core";
import { NmsDialogService } from "@nms-angular-toolkit/nms-dialog";
import { ColumnConfig, ConfirmOptionsModel, NmsTableValidatorService, TextStyleClasses } from "@nms-angular-toolkit/nms-table";
import { ErrorResponseHandlerService } from "@nms-ng2/app/core/services/error-response-handler/error-response-handler.service";
import {
    ANGULARJS_ROOTSCOPE,
    ANGULARJS_TRANSLATE,
    AUTHENTICATION_SERVICE,
    LABEL_COLOR_SERVICE,
    LOADING_PANE_SERVICE,
    NMS_FEATURES,
    NMS_STATES,
    STATE
} from "@nms-ng2/app/shared/services/upgraded-provider/upgraded-providers";
import {
    CIRCUIT_ACTIONS_TRANSLATION_MAP,
    CIRCUIT_STATUS,
    CIRCUIT_STATUS_TRANSLATION_MAP,
    CircuitActions,
    CircuitStatus,
    DwdmCircuitDiagnosticData,
    DwdmCircuitInfo,
    DwdmCircuitsFields,
    DwdmCircuitTranslations
} from "../dwdm-circuit.model";
import { TranslationHelperService } from "@nms-ng2/app/shared/services/util/translation-helper.service";
import { CircuitService } from "./circuit.service";
import { DwdmCircuitDiagnosticComponent } from "../modal/dwdm-circuit-diagnostic.component";
import { ModalFactory } from "@nms-ng2/app/shared/services/modal/modal.factory";
import { NmsToastrService } from "@nms-ng2/app/shared/components/elements/nms-toastr/nms-toastr.service";

/**
 * Serviço responsável pelas configurações da tabela dos circuitos DWDM.
 */
@Injectable({
    providedIn: "root"
})
export class DwdmCircuitTableService {

    reloadListEmmiter = new EventEmitter<boolean>();

    constructor(protected readonly errorResponseHandlerService: ErrorResponseHandlerService,
            protected readonly nmsDialogService: NmsDialogService,
            @Inject(ANGULARJS_TRANSLATE) private readonly translate: any,
            @Inject(LABEL_COLOR_SERVICE) private readonly labelColorService: any,
            @Inject(ModalFactory) private readonly modalFactory: ModalFactory,
            private readonly translationHelper: TranslationHelperService,
            private readonly nmsTableValidatorService: NmsTableValidatorService,
            private readonly circuitService: CircuitService,
            @Inject(LOADING_PANE_SERVICE) private readonly paneService: any,
            private readonly toastrService: NmsToastrService,
            @Inject(STATE) private readonly $state: any,
            @Inject(NMS_STATES) private readonly nmsStates: any,
            @Inject(NMS_FEATURES) private readonly nmsFeatures: any,
            @Inject(AUTHENTICATION_SERVICE) private readonly authenticationService: any,
            @Inject(ANGULARJS_ROOTSCOPE) private readonly rootScope: any) {}

    getColumnsConfig(actions: DwdmCircuitTranslations["actions"], status: DwdmCircuitTranslations["status"]): ColumnConfig[] {
        return [
            {
                field: DwdmCircuitsFields.name,
                label: this.translate.instant("dwdm.circuit.table.name"),
                minWidth: "500px",
                width: "535px",
                alwaysVisible: true,
                optionalColumn: {
                    type: "text",
                    onAction: (row) => this.editInLine(row.resourceId)
                }
            },
            {
                field: DwdmCircuitsFields.aEnd,
                label: this.translate.instant("dwdm.circuit.table.origin"),
                minWidth: "200px",
                width: "300px"
            },
            {
                field: DwdmCircuitsFields.zEnd,
                label: this.translate.instant("dwdm.circuit.table.destination"),
                minWidth: "200px",
                width: "300px"
            },
            {
                field: DwdmCircuitsFields.muxes,
                label: this.translate.instant("dwdm.circuit.table.muxdemux"),
                minWidth: "180px",
                width: "220px",
                optionalColumn: {
                    type: "text",
                    textCustomOptions: {
                        buildCustomText: (row: any) =>
                            !row.muxes ? this.translate.instant("dwdm.circuit.table.not.muxdemux") : row.muxes,
                        customClass: (row: any): TextStyleClasses => (!row.muxes ? "customItalicText" : null)
                    }
                }
            },
            {
                field: DwdmCircuitsFields.status,
                label: this.translate.instant("dwdm.circuit.table.status"),
                minWidth: "150px",
                width: "150px",
                availableValues: [
                    { value: String(CircuitStatus.CRITICAL), label: status.critical },
                    { value: String(CircuitStatus.MAJOR), label: status.major },
                    { value: String(CircuitStatus.MINOR), label: status.minor },
                    { value: String(CircuitStatus.DISABLED), label: status.disabled },
                    { value: String(CircuitStatus.NORMAL), label: status.normal },
                    { value: String(CircuitStatus.UNKNOWN), label: status.unknown }
                ],
                optionalColumn: {
                    type: "status",
                    convert: (status) => {
                        const { translationKey, bgColor } = CIRCUIT_STATUS[status];
                        return {
                            label: this.translate.instant(translationKey),
                            bgColor,
                            color: this.labelColorService.getBestColorContrastForForeground(bgColor)
                        };
                    }
                }
            },
            {
                field: "actions",
                label: this.translate.instant("dwdm.circuit.table.actions"),
                width: "5%",
                optionalColumn: { type: "actions" },
                unsortable: true,
                unsearchable: true,
                actions: [
                    {   id: "edit",
                        iconName: "edit_square",
                        tooltip: actions.edit,
                        onAction: (row) => this.editInLine(row.resourceId)
                    },
                    {
                        id: "diagnostic",
                        iconName: "troubleshoot",
                        tooltip: actions.diagnostic,
                        onAction: (row) => this.openCircuitDiagnosticModal(row.resourceId)
                    },
                    {
                        id: "remove",
                        iconName: "delete",
                        tooltip: actions.remove,
                        onAction: (info) => this.deleteInline(info)
                    }
                ]
            }
        ];
    }

    async getMessageForConfirmOptions(removeFromNms: boolean, circuitsInfo: DwdmCircuitInfo[]) {
        let devicesNames = [];
        if (!removeFromNms) {
            devicesNames = await this.circuitService.retrieveDeviceNames(circuitsInfo[0]).toPromise();
            if (devicesNames.length === 0) {
                throw new Error("Circuit not found.");
            }
        }

        const nmsPart = removeFromNms ? ".from.nms" : "";
        const quantityPart = circuitsInfo.length <= 1 ? ".singleDescription" : ".pluralDescription";
        const translateKey = `dwdm.circuit.table.confirm.remove${nmsPart}${quantityPart}`;

        return {
            title: this.translate.instant("dwdm.circuit.table.confirm.remove.title"),
            description: this.translate.instant(translateKey).replace("{0}", devicesNames.join(" <br> ")),
            bindHtml: true
        } as ConfirmOptionsModel;
    }

    getAllTranslations(): DwdmCircuitTranslations {
        return {
            filter: {
                and: this.translate.instant("nms-table.filter.and"),
                or: this.translate.instant("nms-table.filter.or")
            },
            table: {
                name: this.translate.instant("dwdm.circuit.table.name")
            },
            actions: {
                add: this.translationHelper.translateFromEnum(CIRCUIT_ACTIONS_TRANSLATION_MAP, CircuitActions.ADD),
                edit: this.translationHelper.translateFromEnum(CIRCUIT_ACTIONS_TRANSLATION_MAP, CircuitActions.EDIT),
                diagnostic: this.translationHelper.translateFromEnum(CIRCUIT_ACTIONS_TRANSLATION_MAP, CircuitActions.DIAGNOSTIC),
                remove: this.translationHelper.translateFromEnum(CIRCUIT_ACTIONS_TRANSLATION_MAP, CircuitActions.REMOVE),
                removeFromNms: this.translationHelper.translateFromEnum(
                    CIRCUIT_ACTIONS_TRANSLATION_MAP,
                    CircuitActions.REMOVE_FROM_NMS
                )
            },
            status: {
                critical: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.CRITICAL),
                major: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.MAJOR),
                minor: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.MINOR),
                disabled: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.DISABLED),
                normal: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.NORMAL),
                unknown: this.translationHelper.translateFromEnum(CIRCUIT_STATUS_TRANSLATION_MAP, CircuitStatus.UNKNOWN)
            },
            columnsSettings: {
                iconTooltip: this.translate.instant("nms.table.organize.columns.tooltip"),
                noColumnFound: this.translate.instant("nms.table.organize.columns.search.not.found"),
                showHideColumns: this.translate.instant("nms.table.show.hide.columns.columns"),
                showHideColumnsTooltip: this.translate.instant("nms.table.show.hide.columns.tooltip"),
                searchColumnsPlaceholder: this.translate.instant("nms.table.organize.columns.search"),
                restoreOrdering: this.translate.instant("nms.table.organize.restore.ordering")
            }
        };
    }

    public verifyUserPermission(removeFromNms: boolean): boolean {
        const feature = removeFromNms ? this.nmsFeatures.removeDwdmCircuitFromNms.feature
            : this.nmsFeatures.removeDwdmCircuit.feature;

        if (!this.authenticationService.hasPermission(feature)) {
            this.rootScope.showDialog({
                translateKey: "dwdm.circuit.error.noUserPermission"
            });

            return false;
        }

        return true;
    }

    private async editInLine(circuitId: number) {
        this.$state.go(this.nmsStates.dwdmCircuits.edit, {circuitId});
    }

    private async deleteInline(circuitInfo: DwdmCircuitInfo, removeFromNms: boolean = false) {
        if (this.verifyUserPermission(removeFromNms)) {
            const message = await this.getMessageForConfirmOptions(false, [circuitInfo]);
            this.nmsTableValidatorService.showConfirmOptions(message).then(async () => {
                this.paneService.showLoadingPane("dwdm.circuit.table.removing.item");
                const response = await this.circuitService.removeAndActivate(circuitInfo).toPromise();
                if (response == null) {
                    this.paneService.closeLoadingPane();
                    this.toastrService.success(this.translate.instant("dwdm.circuit.table.item.success.removed"))
                    this.reloadListEmmiter.emit();
                }
            });
        }
    }

    private async openCircuitDiagnosticModal(circuitId: number) {
        this.modalFactory.openSimpleModal(DwdmCircuitDiagnosticComponent, { circuitId });
    }
}
